import { useEffect, useMemo, useRef, useState } from "react";

// Element types order for Tab cycling
export const ELEMENT_TYPES = [
  { id: "scene_heading", label: "Escena", short: "ESC", shortcut: "1" },
  { id: "action", label: "Acción", short: "ACC", shortcut: "2" },
  { id: "character", label: "Personaje", short: "PER", shortcut: "3" },
  { id: "parenthetical", label: "Acotación", short: "ACO", shortcut: "4" },
  { id: "dialogue", label: "Diálogo", short: "DLG", shortcut: "5" },
  { id: "transition", label: "Transición", short: "TRA", shortcut: "6" },
  { id: "shot", label: "Plano", short: "PLN", shortcut: "7" },
  { id: "note", label: "Nota", short: "NOT", shortcut: "8" },
  { id: "subtitle", label: "Sobreimpreso", short: "SOB", shortcut: "9" },
];

const TYPE_BY_ID = Object.fromEntries(ELEMENT_TYPES.map((t) => [t.id, t]));

// Returns the next type that should be picked when user presses Enter after the current one
export function nextTypeAfterEnter(prevType) {
  return {
    scene_heading: "action",
    action: "action",
    character: "dialogue",
    parenthetical: "dialogue",
    dialogue: "action",
    transition: "scene_heading",
    shot: "action",
    note: "action",
    subtitle: "action",
  }[prevType] || "action";
}

// Returns the next type for Tab cycling (forward) within a line
export function cycleType(currentType, dir = 1) {
  const idx = ELEMENT_TYPES.findIndex((t) => t.id === currentType);
  const len = ELEMENT_TYPES.length;
  const next = ELEMENT_TYPES[(idx + dir + len) % len];
  return next.id;
}

// Hollywood / Final Draft inspired layout — measurements in `ch` units so they
// stay precise inside the monospace Courier font used by the editor body.
// (US Letter, Courier 12pt: action spans the full text column; character is at
// ~3.7" from page edge; dialogue at ~2.5"; parenthetical at ~3.1".)
function typeClasses(type) {
  switch (type) {
    case "scene_heading":
      return "font-mono text-[13px] font-bold uppercase tracking-wider text-stone-900 mt-4";
    case "action":
      return "font-mono text-[13px] text-stone-800";
    case "character":
      // ~26ch indent → sits well above the dialogue column, like Final Draft
      return "font-mono text-[13px] font-bold uppercase text-stone-900 mt-2 pl-[26ch]";
    case "parenthetical":
      // padding + max-w are additive in CSS, so max-w must cover both: 20ch pad + ~30ch text = 50ch
      return "font-mono text-[12px] italic text-stone-700 pl-[20ch] max-w-[50ch]";
    case "dialogue":
      // 14ch pad + ~42ch text = 56ch total
      return "font-mono text-[13px] text-stone-800 pl-[14ch] max-w-[56ch]";
    case "transition":
      return "font-mono text-[13px] font-bold uppercase text-stone-900 text-right mt-2";
    case "shot":
      return "font-mono text-[13px] font-bold uppercase text-[#26775E] mt-2";
    case "note":
      return "font-mono text-[12px] italic text-amber-700 bg-amber-50 px-2 py-1 rounded";
    case "subtitle":
      // Sobreimpresión / rótulo en pantalla — bold uppercase, ancho completo,
      // con un acento amarillo a la izquierda para distinguirlo de acción/escena.
      return "font-mono text-[13px] font-bold uppercase tracking-wider text-stone-900 mt-3 border-l-2 border-amber-500 pl-2";
    default:
      return "font-mono text-[13px] text-stone-800";
  }
}

/**
 * Single editable screenplay line. The visible type badge has been removed —
 * line formatting alone communicates the element type, like in Final Draft.
 *
 * Props additions vs older version:
 *  - onRequestTypePicker(): user pressed Enter on an empty line → open picker
 */
export default function ElementLine({
  element,
  sceneNumber,
  selected,
  autoFocus,
  autoFocusCursor = "end",
  characterMatrix = [],
  onChange,
  onEnter,
  onBackspaceEmpty,
  onTab,
  onArrowUp,
  onArrowDown,
  onChangeType,
  onToggleSelect,
  onFocus,
  onRequestTypePicker,
}) {
  const ref = useRef(null);
  // Sugerencia inline de nombre de personaje (suave: solo aparece cuando hay
  // match prefijo con la MATRIZ de personajes — no con nombres sueltos del
  // guion — y solo si el usuario ha escrito ≥2 letras. Tab acepta. NO captura
  // Enter, flechas ni Esc para no interferir con el flow normal del editor).
  const [acDismissed, setAcDismissed] = useState(false);
  const charSuggestion = useMemo(() => {
    if (element.type !== "character" || acDismissed) return null;
    const q = (element.text || "").trim().toUpperCase();
    if (q.length < 2) return null;
    for (const name of characterMatrix) {
      const upper = (name || "").toUpperCase();
      if (upper.startsWith(q) && upper !== q) return upper;
    }
    return null;
  }, [element.type, element.text, characterMatrix, acDismissed]);

  // Si el usuario sigue tecleando, reseteamos el flag de "descartado"
  // (para que vuelva a sugerir tras un Esc previo si el texto cambia).
  useEffect(() => { setAcDismissed(false); }, [element.text, element.type]);

  // Focus & cursor placement: ONLY run when `autoFocus` becomes true (entering
  // this line). Do NOT depend on `element.text`, or the cursor would jump to
  // the end of the line on every keystroke / delete (#bug "salta de párrafo").
  useEffect(() => {
    if (autoFocus && ref.current) {
      ref.current.focus();
      let pos;
      if (autoFocusCursor === "start") pos = 0;
      else if (autoFocusCursor === "paren-inside") {
        // Para acotaciones recién creadas con auto-paréntesis "()". El cursor
        // va entre ambos: posición 1. Si el texto NO empieza con "(" caemos
        // al comportamiento "end" como fallback seguro.
        const txt = ref.current.value || "";
        pos = txt.startsWith("(") ? 1 : txt.length;
      }
      else pos = (ref.current.value || "").length;
      try {
        ref.current.setSelectionRange(pos, pos);
      } catch (_e) {
        // ignore
      }
    }
  }, [autoFocus, autoFocusCursor]);

  // Auto-resize textarea
  useEffect(() => {
    if (!ref.current) return;
    ref.current.style.height = "auto";
    ref.current.style.height = ref.current.scrollHeight + "px";
  }, [element.text, element.type]);

  const isUpper = element.type === "scene_heading" || element.type === "character" ||
                  element.type === "transition" || element.type === "shot" ||
                  element.type === "subtitle";

  const handleKey = (e) => {
    // ── Autocompletado suave de personaje ─────────────────────────────
    // Solo intercepta Tab para aceptar la sugerencia, y Esc para descartar.
    // No tocamos Enter ni flechas — el flow del editor sigue intacto.
    if (charSuggestion) {
      if (e.key === "Tab" && !e.shiftKey) {
        e.preventDefault();
        onChange?.({ text: charSuggestion });
        setAcDismissed(true);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setAcDismissed(true);
        return;
      }
    }

    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      // Final-Draft-style: Enter on an EMPTY line opens the type picker
      // instead of stacking yet another empty line.
      if ((element.text || "") === "") {
        onRequestTypePicker?.();
      } else {
        // Pasamos la posición del cursor para que el padre pueda hacer split
        // del texto en medio del párrafo (en lugar de añadir siempre una
        // acción nueva vacía al final).
        const cursorPos = ref.current?.selectionStart ?? (element.text || "").length;
        onEnter?.(cursorPos);
      }
      return;
    }
    if (e.key === "Tab") {
      e.preventDefault();
      // SHIFT+Tab → cicla al tipo anterior (atajo rápido para usuarios
      // acostumbrados al cycle). TAB solo → abre el TypePicker (estilo
      // Final Draft) con todas las opciones visibles incl. SOBREIMPRESO.
      if (e.shiftKey) {
        onTab?.(-1);
      } else {
        onRequestTypePicker?.();
      }
      return;
    }
    if (e.key === "Backspace" && (element.text || "") === "") {
      e.preventDefault();
      onBackspaceEmpty?.();
      return;
    }
    if (e.key === "ArrowUp" && ref.current?.selectionStart === 0) {
      const textBefore = (element.text || "").slice(0, ref.current.selectionStart);
      if (!textBefore.includes("\n")) {
        e.preventDefault();
        onArrowUp?.();
      }
    }
    if (e.key === "ArrowDown") {
      const cursor = ref.current?.selectionStart ?? 0;
      const text = element.text || "";
      const textAfter = text.slice(cursor);
      if (!textAfter.includes("\n")) {
        e.preventDefault();
        onArrowDown?.();
      }
    }
  };

  const handleInput = (e) => {
    let val = e.target.value;
    if (isUpper) val = val.toUpperCase();
    onChange?.({ text: val });
  };

  const typeMeta = TYPE_BY_ID[element.type] || TYPE_BY_ID.action;

  return (
    <div
      data-testid={`sp-line-${element.id}`}
      data-line-type={element.type}
      className={`group relative flex items-start gap-1 rounded px-2 py-0.5 transition ${
        selected ? "bg-[#26775E]/10 ring-1 ring-[#26775E]/40" : "hover:bg-stone-50/60"
      }`}
    >
      {/* Selection checkbox — visible only on hover or when selected.
          Shift+Click selecciona un rango (entre el último seleccionado y este). */}
      <button
        type="button"
        onClick={(e) => onToggleSelect?.(e.shiftKey)}
        data-testid={`sp-select-${element.id}`}
        title="Click: seleccionar · Shift+Click: rango · Selecciona varios y borra de golpe o reescribe con IA"
        className={`mt-1.5 h-3.5 w-3.5 shrink-0 rounded border ${
          selected
            ? "border-[#26775E] bg-[#26775E]"
            : "border-stone-300 bg-white opacity-0 group-hover:opacity-50"
        }`}
      >
        {selected ? (
          <svg viewBox="0 0 16 16" className="h-2.5 w-2.5 text-white" fill="currentColor">
            <path d="M6 11l-3-3 1.5-1.5L6 8l5.5-5.5L13 4l-7 7z" />
          </svg>
        ) : null}
      </button>

      {/* Scene number gutter — only for scene_heading lines */}
      <div className="mt-1 w-6 shrink-0 text-right text-[10px] font-mono text-stone-400">
        {sceneNumber != null ? sceneNumber : ""}
      </div>

      {/* Text textarea */}
      <textarea
        ref={ref}
        value={element.text || ""}
        onChange={handleInput}
        onKeyDown={handleKey}
        onFocus={onFocus}
        rows={1}
        spellCheck={element.type !== "scene_heading" && element.type !== "transition" && element.type !== "shot"}
        lang="es-ES"
        data-testid={`sp-text-${element.id}`}
        placeholder={typeMeta.label}
        className={`flex-1 resize-none border-0 bg-transparent outline-none placeholder:text-stone-300 ${typeClasses(element.type)}`}
      />

      {/* Píldora de sugerencia de personaje — pequeña, no invasiva, a la
          derecha del campo. Solo aparece cuando `charSuggestion` está
          activa. Tab acepta. Esc descarta. */}
      {charSuggestion && (
        <span
          className="pointer-events-none absolute right-7 top-1.5 z-20 rounded-full border border-stone-300 bg-white px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-stone-500 shadow-sm"
          data-testid={`sp-char-hint-${element.id}`}
        >
          Tab → <strong className="text-[#26775E]">{charSuggestion}</strong>
        </span>
      )}
      {/* Hover-only "change type" chevron — kept as escape hatch for the user. */}
      <button
        type="button"
        onClick={() => onRequestTypePicker?.()}
        data-testid={`sp-changetype-${element.id}`}
        title="Cambiar tipo (o pulsa Tab / Enter en línea vacía)"
        className="mt-1 ml-1 h-4 w-4 shrink-0 rounded text-[10px] text-stone-300 opacity-0 transition hover:bg-stone-200 hover:text-stone-700 group-hover:opacity-100"
      >
        ⌄
      </button>
    </div>
  );
}
